import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  SpinnerGap,
  Envelope,
  PaperPlaneTilt,
  Users,
  Lightning,
  ArrowRight,
  CheckCircle,
  XCircle,
  Clock,
  Warning,
  UserMinus,
  ChartLine,
  Eye,
  Palette,
  Image as ImageIcon,
  ArrowsClockwise,
  Desktop,
  DeviceMobile,
  Lock,
  Flask,
  EnvelopeSimple,
  Tag,
  FloppyDisk,
  Trash,
  FolderOpen,
  Megaphone,
  TextAlignLeft,
  ArrowsOutSimple,
  ArrowsInSimple,
  PencilSimple,
  X,
} from "phosphor-react";
import { useEntitlementsWithFetch } from "../contexts/EntitlementContext";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import BrandKitWizard from "../components/BrandKitWizard";
import EmailTemplatePreview, { TemplateSelector } from "../components/EmailTemplatePreview";
import { authFetchJson } from "../auth/authApi";
import { useIsDesktop, useIsWideDesktop } from "../hooks/useMediaQuery";
import DesktopRequiredMessage from "../components/DesktopRequiredMessage";
import FullScreenPreviewModal from "../components/FullScreenPreviewModal";
import RecipientsDrawer from "../components/RecipientsDrawer";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Debounce delay for preview rendering (ms)
const PREVIEW_DEBOUNCE_MS = 600;

// Quill editor modules configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'align': [] }],
    ['link'],
    ['clean']
  ],
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'list', 'bullet', 'align', 'link'
];

export default function EmailMarketing() {
  const navigate = useNavigate();
  const { isFeatureActive, isEntitled } = useEntitlementsWithFetch();
  
  // Desktop detection - Email Marketing requires desktop for proper builder experience
  const isDesktop = useIsDesktop();
  // Wide desktop detection for optional split layout
  const isWideDesktop = useIsWideDesktop();
  
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [featureInactive, setFeatureInactive] = useState(false);
  const [featureDetails, setFeatureDetails] = useState(null);
  
  // ACTION-BASED GATING: Track entitlement status but don't block page access
  // Users can configure everything - gating only on SEND/SCHEDULE actions
  const [emailMarketingEntitled, setEmailMarketingEntitled] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Brand Kit state
  const [brandKit, setBrandKit] = useState(null);
  const [brandKitComplete, setBrandKitComplete] = useState(false);
  const [showBrandKitWizard, setShowBrandKitWizard] = useState(false);
  
  // Marketing Templates
  const [marketingTemplates, setMarketingTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [brandVersion, setBrandVersion] = useState(0);
  const [updatingBranding, setUpdatingBranding] = useState(false);
  
  // Recipients - now stores both eligible and opted-out lists
  const [eligibleRecipients, setEligibleRecipients] = useState([]);
  const [optedOutRecipients, setOptedOutRecipients] = useState([]);
  const [optedOutCount, setOptedOutCount] = useState(0);
  
  // Campaign form
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");  // Immediate state for editor
  const [committedBody, setCommittedBody] = useState("");  // Debounced state for preview
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  
  // Debounce timer ref for preview updates
  const bodyDebounceRef = useRef(null);
  
  // Company info for preview
  const [companyInfo, setCompanyInfo] = useState({ name: "", logo_url: null });
  
  // Campaigns history
  const [campaigns, setCampaigns] = useState([]);
  
  // Confirm modal
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  
  // Full-screen preview modal (REMOVED - no longer used)
  const [fullScreenPreviewOpen, setFullScreenPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Preview width
  const [previewWidth, setPreviewWidth] = useState("desktop");
  
  // Composer Mode - true compose-only workspace with WYSIWYG template view
  const [focusMode, setFocusMode] = useState(false);
  const [recipientsDrawerOpen, setRecipientsDrawerOpen] = useState(false);
  
  // Campaign report modal
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetails, setCampaignDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Send Test feature
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmailOverride, setTestEmailOverride] = useState("");
  const [showTestEmailInput, setShowTestEmailInput] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [lastOverrideEmail, setLastOverrideEmail] = useState("");

  // Audience Segments (Marketing-Only)
  const [segments, setSegments] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedSegmentId, setSelectedSegmentId] = useState("all_customers");
  const [selectedSegmentTag, setSelectedSegmentTag] = useState("");
  const [segmentRecipientCount, setSegmentRecipientCount] = useState(0);
  const [loadingSegmentCount, setLoadingSegmentCount] = useState(false);

  // Saved Drafts
  const [drafts, setDrafts] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showDraftsModal, setShowDraftsModal] = useState(false);
  const [loadingDrafts, setLoadingDrafts] = useState(false);

  // Scheduled Campaigns Management
  const [scheduledCampaigns, setScheduledCampaigns] = useState([]);
  const [tenantTimezone, setTenantTimezone] = useState("UTC");
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [newScheduledDate, setNewScheduledDate] = useState("");
  const [newScheduledTime, setNewScheduledTime] = useState("");
  const [actionLoading, setActionLoading] = useState(null);

  // Debounced body change handler - updates committedBody after typing stops
  const handleBodyChange = useCallback((newBody) => {
    // Update immediate state (for editor)
    setBody(newBody);
    
    // Clear existing debounce timer
    if (bodyDebounceRef.current) {
      clearTimeout(bodyDebounceRef.current);
    }
    
    // Set new debounce timer for preview update
    bodyDebounceRef.current = setTimeout(() => {
      setCommittedBody(newBody);
    }, PREVIEW_DEBOUNCE_MS);
  }, []);

  // Manual refresh preview - immediately commits body to preview
  const handleRefreshPreview = useCallback(() => {
    if (bodyDebounceRef.current) {
      clearTimeout(bodyDebounceRef.current);
    }
    setCommittedBody(body);
  }, [body]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (bodyDebounceRef.current) {
        clearTimeout(bodyDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const response = await fetch(`${API}/tenants/email-marketing/status`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (!data.feature_active) {
          setFeatureInactive(true);
          setLoading(false);
          return;
        }
        
        // Store entitlement status for action-based gating
        // But DON'T block page access - users can configure everything
        setEmailMarketingEntitled(data.entitled);
        setFeatureDetails(data.feature);
        
        // Load brand kit, recipients, campaigns, templates, and company info
        // This happens regardless of entitlement status
        await Promise.all([
          fetchBrandKit(),
          fetchRecipients(), 
          fetchCampaigns(), 
          fetchTemplates(), 
          fetchCompanyInfo(),
          fetchMarketingTemplates(),
          fetchTestEmailSettings(),
          fetchSegments(),
          fetchDrafts(),
          fetchScheduledCampaigns()
        ]);
      }
    } catch (error) {
      console.error("Error checking email marketing status:", error);
      toast.error("Failed to load email marketing");
    } finally {
      setLoading(false);
    }
  };

  const fetchBrandKit = async () => {
    try {
      // Use authFetchJson for Bearer token authentication
      const data = await authFetchJson('/tenants/brand-kit');
      
      // Merge tenant_logo into brand_kit if brand_kit doesn't have its own logo
      // This ensures canonical tenant logo is used as fallback
      if (data.brand_kit && !data.brand_kit.logo_url && data.tenant_logo) {
        data.brand_kit.logo_url = data.tenant_logo;
      }
      
      setBrandKit(data.brand_kit);
      
      // Brand kit is complete if it has an effective logo (brand kit or tenant)
      // The API's is_complete already checks for logo_url, which includes tenant_logo fallback
      const effectiveLogoExists = data.brand_kit?.logo_url || data.tenant_logo;
      const isComplete = data.is_complete || (effectiveLogoExists && data.brand_kit?.primary_color && data.brand_kit?.business_name);
      setBrandKitComplete(isComplete);
      
      // If brand kit is incomplete, show wizard
      if (!isComplete) {
        setShowBrandKitWizard(true);
      }
    } catch (error) {
      // Handle errors gracefully - don't block setup
      // A 403 error should NOT be treated as fatal
      console.error("Error fetching brand kit:", error);
      // Still show wizard on error so user can set up
      setShowBrandKitWizard(true);
    }
  };

  const fetchMarketingTemplates = async () => {
    try {
      // Use authFetchJson for Bearer token authentication
      const data = await authFetchJson('/tenants/marketing-templates');
      if (data.entitled) {
        setMarketingTemplates(data.templates || []);
        setBrandVersion(data.brand_version || 0);
        
        // Auto-select first template if none selected
        if (!selectedTemplateId && data.templates?.length > 0) {
          setSelectedTemplateId(data.templates[0].template_id);
        }
      }
    } catch (error) {
      console.error("Error fetching marketing templates:", error);
    }
  };

  const handleBrandKitComplete = async () => {
    setShowBrandKitWizard(false);
    setBrandKitComplete(true);
    // Refresh brand kit and templates
    await Promise.all([fetchBrandKit(), fetchMarketingTemplates()]);
    toast.success("Brand Kit setup complete!");
  };

  const handleUpdateAllBranding = async () => {
    setUpdatingBranding(true);
    try {
      const response = await fetch(`${API}/tenants/marketing-templates/update-all-branding`, {
        method: "POST",
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.templates_updated} templates updated!`);
        await fetchMarketingTemplates();
      } else {
        toast.error("Failed to update branding");
      }
    } catch (error) {
      toast.error("Failed to update branding");
    } finally {
      setUpdatingBranding(false);
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      // Use authFetchJson for Bearer token authentication
      const tenant = await authFetchJson('/tenants/current');
      const logoData = await authFetchJson('/tenants/logo');
      
      setCompanyInfo({
        name: tenant.company_name || "Your Company",
        logo_url: logoData.has_logo ? logoData.logo_url : null
      });
    } catch (error) {
      console.error("Error fetching company info:", error);
    }
  };

  const fetchTemplates = async () => {
    // Legacy template fetch - no longer used since we use marketingTemplates
    // Kept for backwards compatibility but does nothing
    try {
      const response = await fetch(`${API}/tenants/email-marketing/templates`, {
        credentials: "include",
      });
      
      if (response.ok) {
        // Templates are now managed via marketingTemplates state
        // This endpoint is deprecated
      }
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const fetchRecipients = async () => {
    try {
      const response = await fetch(`${API}/tenants/email-marketing/recipients`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.entitled) {
          // New API returns both lists
          setEligibleRecipients(data.eligible || []);
          setOptedOutRecipients(data.opted_out || []);
          setOptedOutCount(data.opted_out_count || 0);
        }
      }
    } catch (error) {
      console.error("Error fetching recipients:", error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await fetch(`${API}/tenants/email-marketing/campaigns?limit=5`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.entitled) {
          setCampaigns(data.campaigns || []);
        }
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    }
  };

  const fetchTestEmailSettings = async () => {
    try {
      const data = await authFetchJson('/email-marketing/test-email-settings');
      setUserEmail(data.user_email || "");
      setLastOverrideEmail(data.last_override_email || "");
      // Pre-fill override input with last used value
      if (data.last_override_email) {
        setTestEmailOverride(data.last_override_email);
      }
    } catch (error) {
      console.error("Error fetching test email settings:", error);
    }
  };

  const handleSendTest = async () => {
    // Validate content
    if (!subject.trim()) {
      toast.error("Please enter a subject before sending a test");
      return;
    }
    
    const textLength = getPlainTextLength(body);
    if (textLength < 1) {
      toast.error("Please enter some content in the email body");
      return;
    }

    // Determine recipient
    const effectiveOverride = showTestEmailInput ? testEmailOverride.trim() : "";
    const recipient = effectiveOverride || userEmail;

    if (!recipient) {
      toast.error("No email address available. Please enter an override email.");
      setShowTestEmailInput(true);
      return;
    }

    setSendingTest(true);
    try {
      const response = await authFetchJson('/email-marketing/send-test', {
        method: "POST",
        body: JSON.stringify({
          template_id: selectedTemplateId || null,
          subject: subject.trim(),
          body: body,
          override_email: effectiveOverride || null
        })
      });

      if (response.success) {
        toast.success(
          <div>
            <p className="font-medium">Test email sent!</p>
            <p className="text-sm text-zinc-600">Check inbox at {response.sent_to}</p>
          </div>
        );
        // Update last override if used
        if (effectiveOverride) {
          setLastOverrideEmail(effectiveOverride);
        }
      } else {
        toast.error(response.message || "Failed to send test email");
      }
    } catch (error) {
      // Handle rate limit specifically
      if (error.message?.includes("Rate limit")) {
        toast.error(error.message, { duration: 5000 });
      } else {
        toast.error(error.message || "Failed to send test email");
      }
    } finally {
      setSendingTest(false);
    }
  };

  // ============================================
  // AUDIENCE SEGMENTS (Marketing-Only)
  // ============================================
  
  const fetchSegments = async () => {
    try {
      const data = await authFetchJson('/email-marketing/segments');
      setSegments(data.segments || []);
      setAvailableTags(data.available_tags || []);
    } catch (error) {
      console.error("Error fetching segments:", error);
    }
  };

  const fetchSegmentPreview = async (segmentId, tag = null) => {
    setLoadingSegmentCount(true);
    try {
      const url = tag 
        ? `/email-marketing/segments/${segmentId}/preview?tag=${encodeURIComponent(tag)}`
        : `/email-marketing/segments/${segmentId}/preview`;
      const data = await authFetchJson(url);
      setSegmentRecipientCount(data.recipient_count || 0);
    } catch (error) {
      console.error("Error fetching segment preview:", error);
      setSegmentRecipientCount(0);
    } finally {
      setLoadingSegmentCount(false);
    }
  };

  // Fetch full segment recipients for modal (includes opted-out list)
  const fetchSegmentRecipientsForModal = async () => {
    try {
      const tag = selectedSegmentId === "by_tag" ? selectedSegmentTag : null;
      const url = tag 
        ? `/email-marketing/segments/${selectedSegmentId}/preview?tag=${encodeURIComponent(tag)}&full_list=true`
        : `/email-marketing/segments/${selectedSegmentId}/preview?full_list=true`;
      const data = await authFetchJson(url);
      
      // Update recipient lists from segment-specific data
      setEligibleRecipients(data.recipients || []);
      setOptedOutRecipients(data.opted_out_recipients || []);
      setOptedOutCount(data.opted_out_count || 0);
      setSegmentRecipientCount(data.recipient_count || 0);
    } catch (error) {
      console.error("Error fetching segment recipients:", error);
    }
  };

  // Handle opening recipients modal - fetch segment-specific data
  const handleOpenRecipientsModal = async () => {
    await fetchSegmentRecipientsForModal();
    setRecipientsDrawerOpen(true);
  };

  // Update segment count when segment or tag changes
  useEffect(() => {
    if (selectedSegmentId) {
      const tag = selectedSegmentId === "by_tag" ? selectedSegmentTag : null;
      if (selectedSegmentId !== "by_tag" || tag) {
        fetchSegmentPreview(selectedSegmentId, tag);
      } else {
        setSegmentRecipientCount(0);
      }
    }
  }, [selectedSegmentId, selectedSegmentTag]);

  // ============================================
  // SAVED DRAFTS
  // ============================================
  
  const fetchDrafts = async () => {
    setLoadingDrafts(true);
    try {
      const data = await authFetchJson('/email-marketing/drafts');
      setDrafts(data.drafts || []);
    } catch (error) {
      console.error("Error fetching drafts:", error);
    } finally {
      setLoadingDrafts(false);
    }
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const draftData = {
        template_id: selectedTemplateId,
        subject: subject,
        body: body,
        segment_id: selectedSegmentId,
        segment_tag: selectedSegmentId === "by_tag" ? selectedSegmentTag : null
      };

      let response;
      if (currentDraftId) {
        // Update existing draft
        response = await authFetchJson(`/email-marketing/drafts/${currentDraftId}`, {
          method: "PUT",
          body: JSON.stringify(draftData)
        });
      } else {
        // Create new draft
        response = await authFetchJson('/email-marketing/drafts', {
          method: "POST",
          body: JSON.stringify(draftData)
        });
        setCurrentDraftId(response.draft_id);
      }

      toast.success("Draft saved");
      await fetchDrafts();
    } catch (error) {
      toast.error("Failed to save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const handleLoadDraft = async (draftId) => {
    try {
      const data = await authFetchJson(`/email-marketing/drafts/${draftId}`);
      const draft = data.draft;
      
      setSelectedTemplateId(draft.template_id || null);
      setSubject(draft.subject || "");
      setBody(draft.body || "");
      setCommittedBody(draft.body || "");  // Sync committed body for preview
      setSelectedSegmentId(draft.segment_id || "all_customers");
      setSelectedSegmentTag(draft.segment_tag || "");
      setCurrentDraftId(draftId);
      setShowDraftsModal(false);
      
      toast.success(`Loaded draft: ${draft.name}`);
    } catch (error) {
      toast.error("Failed to load draft");
    }
  };

  const handleDeleteDraft = async (draftId) => {
    if (!confirm("Delete this draft?")) return;
    
    try {
      await authFetchJson(`/email-marketing/drafts/${draftId}`, {
        method: "DELETE"
      });
      
      toast.success("Draft deleted");
      if (currentDraftId === draftId) {
        setCurrentDraftId(null);
      }
      await fetchDrafts();
    } catch (error) {
      toast.error("Failed to delete draft");
    }
  };

  const handleNewDraft = () => {
    setCurrentDraftId(null);
    setSubject("");
    setBody("");
    setCommittedBody("");  // Sync committed body for preview
    setSelectedTemplateId(marketingTemplates[0]?.template_id || null);
    setSelectedSegmentId("all_customers");
    setSelectedSegmentTag("");
  };

  // Get segment name for display
  const getSegmentDisplayName = () => {
    const segment = segments.find(s => s.id === selectedSegmentId);
    if (selectedSegmentId === "by_tag" && selectedSegmentTag) {
      return `Tag: ${selectedSegmentTag}`;
    }
    return segment?.name || "All Customers";
  };

  // ============================================
  // SCHEDULED CAMPAIGNS MANAGEMENT
  // ============================================

  const fetchScheduledCampaigns = async () => {
    try {
      const data = await authFetchJson('/email-marketing/campaigns/scheduled');
      setScheduledCampaigns(data.campaigns || []);
      setTenantTimezone(data.tenant_timezone || "UTC");
    } catch (error) {
      console.error("Error fetching scheduled campaigns:", error);
    }
  };

  const handleCancelScheduledCampaign = async (campaignId) => {
    if (!confirm("Cancel this scheduled campaign? It will not be sent.")) return;
    
    setActionLoading(campaignId);
    try {
      await authFetchJson(`/email-marketing/campaigns/${campaignId}/cancel`, {
        method: "POST"
      });
      
      toast.success("Campaign cancelled");
      await Promise.all([fetchScheduledCampaigns(), fetchCampaigns()]);
    } catch (error) {
      toast.error(error.message || "Failed to cancel campaign");
    } finally {
      setActionLoading(null);
    }
  };

  const handleOpenReschedule = (campaign) => {
    setRescheduleTarget(campaign);
    // Pre-fill with current scheduled time
    const scheduledDate = new Date(campaign.scheduled_for);
    setNewScheduledDate(scheduledDate.toISOString().split('T')[0]);
    setNewScheduledTime(scheduledDate.toTimeString().slice(0, 5));
    setShowRescheduleModal(true);
  };

  const handleReschedule = async () => {
    if (!rescheduleTarget || !newScheduledDate || !newScheduledTime) {
      toast.error("Please select a new date and time");
      return;
    }

    const newDateTime = new Date(`${newScheduledDate}T${newScheduledTime}`);
    if (newDateTime <= new Date()) {
      toast.error("Scheduled time must be in the future");
      return;
    }

    setActionLoading(rescheduleTarget.campaign_id);
    try {
      await authFetchJson(`/email-marketing/campaigns/${rescheduleTarget.campaign_id}/reschedule`, {
        method: "PUT",
        body: JSON.stringify({
          scheduled_for: newDateTime.toISOString()
        })
      });
      
      toast.success("Campaign rescheduled");
      setShowRescheduleModal(false);
      setRescheduleTarget(null);
      await fetchScheduledCampaigns();
    } catch (error) {
      toast.error(error.message || "Failed to reschedule campaign");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRetryFailedCampaign = async (campaignId) => {
    if (!confirm("Retry this failed campaign? A new campaign will be created.")) return;
    
    setActionLoading(campaignId);
    try {
      const result = await authFetchJson(`/email-marketing/campaigns/${campaignId}/retry`, {
        method: "POST"
      });
      
      toast.success(`Retry campaign created: ${result.new_campaign_id}`);
      await fetchCampaigns();
    } catch (error) {
      toast.error(error.message || "Failed to retry campaign");
    } finally {
      setActionLoading(null);
    }
  };

  // Format date for display in tenant timezone
  const formatScheduledDate = (isoDate) => {
    try {
      const date = new Date(isoDate);
      return date.toLocaleString('en-US', {
        timeZone: tenantTimezone,
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return isoDate;
    }
  };

  const toggleOptOut = async (customerId, currentStatus) => {
    try {
      const response = await fetch(
        `${API}/tenants/email-marketing/customer/${customerId}/opt-out?opt_out=${!currentStatus}`,
        {
          method: "PUT",
          credentials: "include",
        }
      );
      
      if (response.ok) {
        await fetchRecipients();
        toast.success(currentStatus ? "Customer opted back in" : "Customer opted out");
      }
    } catch (error) {
      toast.error("Failed to update preference");
    }
  };

  const fetchCampaignDetails = async (campaignId) => {
    setLoadingDetails(true);
    try {
      const response = await fetch(`${API}/tenants/email-marketing/campaigns/${campaignId}`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        setCampaignDetails(data);
      }
    } catch (error) {
      console.error("Error fetching campaign details:", error);
      toast.error("Failed to load campaign details");
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (selectedCampaign) {
      fetchCampaignDetails(selectedCampaign);
    } else {
      setCampaignDetails(null);
    }
  }, [selectedCampaign]);

  // Generate preview HTML for the email (legacy - now using server-side preview)
  // This is kept for the confirmation modal display only
  const getSelectedTemplateName = useMemo(() => {
    const template = marketingTemplates.find(t => t.template_id === selectedTemplateId);
    return template?.name || null;
  }, [selectedTemplateId, marketingTemplates]);

  // Strip HTML for validation (get text content only)
  const getPlainTextLength = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || div.innerText || '').trim().length;
  };

  const handleSend = async () => {
    // ACTION-BASED GATING: Check entitlement only when user tries to SEND/SCHEDULE
    if (!emailMarketingEntitled) {
      setShowUpgradeModal(true);
      return;
    }
    
    if (!subject.trim()) {
      toast.error("Please enter a subject");
      return;
    }
    
    const textLength = getPlainTextLength(body);
    if (textLength < 10) {
      toast.error("Please enter at least 10 characters in the email body");
      return;
    }
    
    // Validate segment has recipients
    if (segmentRecipientCount === 0) {
      toast.error("Selected segment has no eligible recipients");
      return;
    }
    
    // Validate tag selection if by_tag segment
    if (selectedSegmentId === "by_tag" && !selectedSegmentTag) {
      toast.error("Please select a tag for the segment");
      return;
    }
    
    // Validate scheduling if enabled
    if (isScheduled) {
      if (!scheduledDate || !scheduledTime) {
        toast.error("Please select both date and time for scheduling");
        return;
      }
      const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      if (scheduledDateTime <= new Date()) {
        toast.error("Scheduled time must be in the future");
        return;
      }
    }
    
    setConfirmModalOpen(true);
  };

  const confirmSend = async () => {
    setConfirmModalOpen(false);
    setSending(true);
    
    try {
      // Build request body - always HTML since we use rich text editor
      const requestBody = {
        subject: subject.trim(),
        body: body,
        body_type: "html",
        template_id: selectedTemplateId || null,
        segment_id: selectedSegmentId,
        segment_tag: selectedSegmentId === "by_tag" ? selectedSegmentTag : null,
        draft_id: currentDraftId,
      };
      
      // Add scheduling if enabled
      if (isScheduled && scheduledDate && scheduledTime) {
        requestBody.scheduled_for = new Date(`${scheduledDate}T${scheduledTime}`).toISOString();
      }
      
      const response = await fetch(`${API}/tenants/email-marketing/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(requestBody),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Check if this was a scheduled campaign
        if (data.status === "scheduled") {
          toast.success(`Campaign scheduled for ${new Date(data.scheduled_for).toLocaleString()}`);
          setSubject("");
          setBody("");
          setCommittedBody("");  // Sync committed body
          setIsScheduled(false);
          setScheduledDate("");
          setScheduledTime("");
          await fetchCampaigns();
        } else if (data.success && data.sent > 0) {
          if (data.failed > 0) {
            // Partial success
            toast.warning(
              <div>
                <p>Campaign partially sent: {data.sent} delivered, {data.failed} failed</p>
                {data.campaign_id && (
                  <button 
                    className="text-xs text-[#FF6600] underline mt-1"
                    onClick={() => navigate(`/email-marketing/report/${data.campaign_id}`)}
                  >
                    View send report
                  </button>
                )}
              </div>
            );
          } else {
            // Full success
            toast.success(`Campaign sent! ${data.sent} emails delivered`);
          }
          setSubject("");
          setBody("");
          setCommittedBody("");  // Sync committed body
          await fetchCampaigns();
        } else {
          // All failed
          const errorMsg = data.first_error || data.error || "Unknown error";
          toast.error(
            <div>
              <p>Campaign failed: {errorMsg}</p>
              {data.campaign_id && (
                <button 
                  className="text-xs text-[#FF6600] underline mt-1"
                  onClick={() => setSelectedCampaign(data.campaign_id)}
                >
                  View send report
                </button>
              )}
            </div>
          );
          await fetchCampaigns();
        }
      } else {
        toast.error(data.detail || data.error || "Failed to send campaign");
      }
    } catch (error) {
      toast.error("Failed to send campaign - network error");
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Desktop gate - Email Marketing builder requires desktop for proper experience
  if (!isDesktop) {
    return (
      <DesktopRequiredMessage
        featureName="Email Marketing"
        title="Desktop Required"
        message="Email Marketing is available on desktop only. Please use a computer to create and preview email campaigns."
        fallbackPath="/dashboard"
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SpinnerGap size={32} className="animate-spin text-[#FF6600]" />
      </div>
    );
  }

  // Feature globally inactive
  if (featureInactive) {
    return (
      <div className="space-y-6" data-testid="email-marketing-inactive">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/settings")}
            className="border-2 border-zinc-200"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">
              Email Marketing
            </h1>
            <p className="text-zinc-500">Feature not available</p>
          </div>
        </div>
        
        <Card className="border-2 border-zinc-200">
          <CardContent className="p-8 text-center">
            <Envelope size={48} className="mx-auto text-zinc-300 mb-4" />
            <h2 className="font-heading text-lg font-bold uppercase mb-2">Coming Soon</h2>
            <p className="text-zinc-500">Email marketing is not currently available.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ACTION-BASED GATING: Page-level blocking removed
  // Users can access all setup, configuration, drafts, etc.
  // Gating happens only on SEND/SCHEDULE actions via showUpgradeModal

  // User is entitled OR not entitled - show full marketing UI either way
  // But first check if Brand Kit is complete
  if (showBrandKitWizard || !brandKitComplete) {
    return (
      <div className="space-y-6" data-testid="brand-kit-setup">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/settings")}
            className="border-2 border-zinc-200"
          >
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">
              Email Marketing
            </h1>
            <p className="text-zinc-500">Set up your brand before sending emails</p>
          </div>
        </div>

        <BrandKitWizard 
          onComplete={handleBrandKitComplete}
          initialBrandKit={brandKit}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-marketing-page">
      {/* Composer Mode renders a streamlined compose-only interface with WYSIWYG template frame */}
      {focusMode ? (
        <>
          {/* Composer Mode Header - Sticky action bar */}
          <div className="sticky top-0 z-30 -mx-4 lg:-mx-8 px-4 lg:px-8 py-3 bg-zinc-900 border-b border-zinc-700 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setFocusMode(false)}
                className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                data-testid="exit-focus-btn"
              >
                <X size={18} className="mr-2" />
                Exit Composer
              </Button>
              <span className="text-sm text-zinc-400">
                Composer Mode — Edit your email in template
              </span>
            </div>
            <div className="flex items-center gap-2">
              {/* Preview button removed - WYSIWYG editing shows template inline */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveDraft}
                disabled={savingDraft}
                className="border-zinc-600 text-zinc-200 hover:bg-zinc-800 hover:text-white"
                data-testid="focus-save-draft-btn"
              >
                {savingDraft ? (
                  <SpinnerGap size={16} className="animate-spin" />
                ) : (
                  <>
                    <FloppyDisk size={16} className="mr-2" />
                    Save
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSendTest}
                disabled={sendingTest || (!userEmail && !testEmailOverride.trim())}
                className="border-zinc-600 text-zinc-200 hover:bg-zinc-800 hover:text-white"
                data-testid="focus-send-test-btn"
              >
                {sendingTest ? (
                  <SpinnerGap size={16} className="animate-spin" />
                ) : (
                  <>
                    <Flask size={16} className="mr-2" />
                    Test
                  </>
                )}
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending || segmentRecipientCount === 0}
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
                data-testid="focus-send-btn"
              >
                {sending ? (
                  <SpinnerGap size={16} className="animate-spin" />
                ) : (
                  <>
                    <PaperPlaneTilt size={16} className="mr-2" />
                    {isScheduled ? "Schedule" : "Send Now"}
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Composer Mode Content - SINGLE EMAIL CANVAS (In-Template Editing) */}
          <div 
            className="min-h-screen -mx-4 lg:-mx-8 px-4 lg:px-8 py-6"
            style={{ backgroundColor: "#e5e7eb" }}
          >
            {/* Current Draft Indicator */}
            {currentDraftId && (
              <div className="max-w-[640px] mx-auto mb-4">
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <FloppyDisk size={18} className="text-amber-600" />
                  <span className="text-sm text-amber-800">
                    Editing: <strong>{drafts.find(d => d.draft_id === currentDraftId)?.name || "Untitled"}</strong>
                  </span>
                </div>
              </div>
            )}

            {/* Subject Input - Above the email canvas */}
            <div className="max-w-[640px] mx-auto mb-4">
              <div className="flex items-center gap-3 bg-white rounded-lg shadow-sm p-3 border border-zinc-200">
                <Label className="text-sm font-medium text-zinc-600 whitespace-nowrap">Subject:</Label>
                <Input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Enter your email subject..."
                  className="flex-1 border-0 shadow-none focus-visible:ring-0 text-base"
                  data-testid="focus-subject-input"
                />
              </div>
            </div>

            {/* THE EMAIL CANVAS - Single 600px frame with in-place editing */}
            {(() => {
              // Get the selected template to determine style
              const selectedTemplate = marketingTemplates.find(t => t.template_id === selectedTemplateId);
              const templateType = selectedTemplate?.template_type || "default";
              const primaryColor = brandKit?.primary_color || "#f8fafc";
              const accentColor = brandKit?.accent_color || "#FF6600";
              const textColor = brandKit?.text_color || "#1f2937";

              // Template-specific header styles (matching backend render logic)
              const getHeaderStyle = () => {
                switch (templateType) {
                  case "promotional":
                    return {
                      background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})`,
                      padding: "30px",
                      textAlign: "center",
                    };
                  case "newsletter":
                    return {
                      padding: "30px",
                      borderBottom: `2px solid ${primaryColor}`,
                      textAlign: "center",
                      backgroundColor: "#ffffff",
                    };
                  case "announcement":
                    return {
                      padding: "30px",
                      textAlign: "center",
                      backgroundColor: "#ffffff",
                    };
                  default:
                    return {
                      padding: "25px 30px",
                      borderBottom: `2px solid ${primaryColor}`,
                      textAlign: "center",
                      backgroundColor: "#ffffff",
                    };
                }
              };

              // Template-specific footer styles
              const getFooterStyle = () => {
                switch (templateType) {
                  case "promotional":
                    return {
                      backgroundColor: "#f8f8f8",
                      padding: "20px 30px",
                      textAlign: "center",
                      borderTop: `3px solid ${primaryColor}`,
                    };
                  case "newsletter":
                    return {
                      backgroundColor: "#333333",
                      padding: "25px 30px",
                      textAlign: "center",
                    };
                  case "announcement":
                    return {
                      backgroundColor: "#fafafa",
                      padding: "20px 30px",
                      textAlign: "center",
                      borderTop: "1px solid #eeeeee",
                    };
                  default:
                    return {
                      backgroundColor: "#f8f8f8",
                      padding: "20px 30px",
                      textAlign: "center",
                      borderTop: "1px solid #eee",
                    };
                }
              };

              // Is footer dark (for text color adjustments)
              const isFooterDark = templateType === "newsletter";

              return (
                <div 
                  className="mx-auto bg-white rounded-lg shadow-xl overflow-hidden"
                  style={{ 
                    maxWidth: "640px",
                    borderTop: templateType === "announcement" ? `5px solid ${primaryColor}` : "none",
                  }}
                  data-testid="composer-template-frame"
                >
                  {/* ══════════════════════════════════════════════════════════════
                      EMAIL TEMPLATE HEADER - Style varies by template_type
                      Matches backend render logic for WYSIWYG accuracy
                      ══════════════════════════════════════════════════════════════ */}
                  <div style={getHeaderStyle()}>
                    {/* Contractor Logo - Same rendering for all templates */}
                    {brandKit?.logo_url ? (
                      <img 
                        src={brandKit.logo_url} 
                        alt={brandKit?.company_name || "Company Logo"}
                        className="mx-auto mb-3"
                        style={{ 
                          maxHeight: "48px", 
                          maxWidth: "200px",
                          display: "block",
                        }}
                      />
                    ) : (
                      <div 
                        className="inline-block px-6 py-3 rounded-lg font-bold text-xl"
                        style={{ 
                          backgroundColor: templateType === "promotional" ? "rgba(255,255,255,0.2)" : accentColor,
                          color: "#ffffff"
                        }}
                      >
                        {brandKit?.company_name || "Your Company"}
                      </div>
                    )}
                    
                    {/* Announcement Badge */}
                    {templateType === "announcement" && (
                      <div 
                        className="inline-block px-4 py-1 rounded-full text-xs uppercase tracking-wider font-medium mt-4"
                        style={{ 
                          backgroundColor: primaryColor,
                          color: "#ffffff",
                        }}
                      >
                        Announcement
                      </div>
                    )}

                    {/* Subject displayed in template header */}
                    {subject && (
                      <h1 
                        className="mt-4 text-xl font-semibold"
                        style={{ 
                          color: templateType === "promotional" ? "#ffffff" : (templateType === "newsletter" ? primaryColor : textColor),
                          borderLeft: templateType === "newsletter" ? `4px solid ${primaryColor}` : "none",
                          paddingLeft: templateType === "newsletter" ? "15px" : "0",
                          textAlign: templateType === "newsletter" ? "left" : "center",
                          marginTop: templateType === "announcement" ? "15px" : "16px",
                        }}
                      >
                        {subject}
                      </h1>
                    )}
                  </div>

                  {/* ══════════════════════════════════════════════════════════════
                      EMAIL BODY - In-Place Quill Editor
                      This is where the user types - directly inside the template
                      ══════════════════════════════════════════════════════════════ */}
                  <div 
                    className="px-6 py-8"
                    style={{ 
                      backgroundColor: "#ffffff",
                      minHeight: "350px",
                      padding: templateType === "announcement" ? "20px 40px 40px" : "30px",
                    }}
                  >
                    <ReactQuill
                      theme="snow"
                      value={body}
                      onChange={handleBodyChange}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Write your email content here..."
                      className="in-template-quill"
                      data-testid="focus-body-editor"
                    />
                  </div>

                  {/* ══════════════════════════════════════════════════════════════
                      EMAIL TEMPLATE FOOTER - Style varies by template_type
                      Matches backend render logic for WYSIWYG accuracy
                      ══════════════════════════════════════════════════════════════ */}
                  <div style={getFooterStyle()}>
                    <p className={`text-xs mb-2 ${isFooterDark ? "text-zinc-400" : "text-zinc-500"}`}>
                      You're receiving this email because you're a valued customer of {brandKit?.company_name || "our company"}.
                    </p>
                    <p className={`text-xs ${isFooterDark ? "text-zinc-500" : "text-zinc-400"}`}>
                      <span className={`underline cursor-pointer ${isFooterDark ? "hover:text-zinc-300" : "hover:text-zinc-600"}`}>Unsubscribe</span>
                      <span className="mx-2">•</span>
                      <span>{brandKit?.company_name || "Your Company"}</span>
                    </p>
                    {brandKit?.address && (
                      <p className={`text-xs mt-1 ${isFooterDark ? "text-zinc-500" : "text-zinc-400"}`}>{brandKit.address}</p>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Scheduling Option - Below the email canvas */}
            <div className="max-w-[640px] mx-auto mt-4">
              <div className="bg-white rounded-lg shadow-sm p-4 border border-zinc-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock size={18} className="text-zinc-500" />
                    <Label className="font-medium text-sm">Schedule for Later</Label>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isScheduled}
                      onChange={(e) => setIsScheduled(e.target.checked)}
                      className="sr-only peer"
                      data-testid="focus-schedule-toggle"
                    />
                    <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF6600]"></div>
                  </label>
                </div>
                {isScheduled && (
                  <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-zinc-100">
                    <div>
                      <Label className="text-xs text-zinc-500">Date</Label>
                      <Input
                        type="date"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">Time</Label>
                      <Input
                        type="time"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Normal Mode Header */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/settings")}
                className="border-2 border-zinc-200"
                data-testid="back-btn"
              >
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">
                  Email Marketing
                </h1>
                <p className="text-zinc-500">Send branded emails to your customers</p>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              {/* Composer Mode Toggle */}
              <Button
                variant="outline"
                onClick={() => setFocusMode(true)}
                className="border-2"
                data-testid="focus-mode-toggle"
              >
                <PencilSimple size={18} className="mr-2" />
                Composer Mode
              </Button>
              
              {/* Draft buttons */}
              <Button
                variant="outline"
                onClick={() => setShowDraftsModal(true)}
                className="border-2"
                data-testid="open-drafts-btn"
              >
                <FolderOpen size={18} className="mr-2" />
                Drafts {drafts.length > 0 && `(${drafts.length})`}
              </Button>
              <Button
                variant="outline"
                onClick={handleSaveDraft}
                disabled={savingDraft}
                className="border-2"
                data-testid="save-draft-btn"
              >
                {savingDraft ? (
                  <SpinnerGap size={18} className="animate-spin" />
                ) : (
                  <>
                    <FloppyDisk size={18} className="mr-2" />
                    {currentDraftId ? "Update Draft" : "Save Draft"}
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowBrandKitWizard(true)}
                className="border-2"
              >
                <Palette size={18} className="mr-2" />
                Brand Kit
              </Button>
            </div>
          </div>

          {/* Current Draft Indicator */}
          {currentDraftId && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <FloppyDisk size={18} className="text-amber-600" />
              <span className="text-sm text-amber-800">
                Editing draft: <strong>{drafts.find(d => d.draft_id === currentDraftId)?.name || "Untitled"}</strong>
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNewDraft}
                className="ml-auto text-xs text-amber-600 hover:text-amber-800"
              >
                New Email
              </Button>
            </div>
          )}

          {/* Main Content - Single column layout for most screens, optional split on very wide */}
          <div className="space-y-6">
          
          {/* Compact Recipients Summary (always visible) */}
          <div className="flex items-center justify-between p-4 bg-white border-2 border-zinc-200 rounded-lg">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-[#FF6600]/10 flex items-center justify-center">
                <Users size={20} className="text-[#FF6600]" />
              </div>
              <div>
                <p className="font-medium text-sm">
                  Recipients: <span className="text-[#FF6600]">{segmentRecipientCount}</span> eligible
                  {optedOutCount > 0 && (
                    <span className="text-zinc-400"> • {optedOutCount} opted out</span>
                  )}
                </p>
                <p className="text-xs text-zinc-500">
                  Segment: {getSegmentDisplayName()}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenRecipientsModal}
              className="border-2"
              data-testid="manage-recipients-btn"
            >
              <Users size={16} className="mr-2" />
              Manage Recipients
            </Button>
          </div>
          
          {/* Template Selection Card */}
          <Card className="border-2 border-zinc-200">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                <ImageIcon size={20} className="text-[#FF6600]" />
                Select Template
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TemplateSelector
                templates={marketingTemplates}
                selectedId={selectedTemplateId}
                onSelect={setSelectedTemplateId}
                brandVersion={brandVersion}
                onUpdateBranding={handleUpdateAllBranding}
                loading={updatingBranding}
              />
            </CardContent>
          </Card>

          {/* Marketing Segment Selector */}
          <Card className="border-2 border-zinc-200">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                <Users size={20} className="text-[#FF6600]" />
                Marketing Segment
              </CardTitle>
              <CardDescription>
                Select which customers will receive this marketing email
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                {segments.map((segment) => (
                  <button
                    key={segment.id}
                    onClick={() => {
                      setSelectedSegmentId(segment.id);
                      if (segment.id !== "by_tag") {
                        setSelectedSegmentTag("");
                      }
                    }}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      selectedSegmentId === segment.id
                        ? "border-[#FF6600] bg-[#FF6600]/5"
                        : "border-zinc-200 hover:border-zinc-300"
                    }`}
                    data-testid={`segment-${segment.id}`}
                  >
                    <div className="flex items-center gap-2">
                      {segment.icon === "users" && <Users size={16} className="text-zinc-500" />}
                      {segment.icon === "lightning" && <Lightning size={16} className="text-emerald-500" />}
                      {segment.icon === "clock" && <Clock size={16} className="text-amber-500" />}
                      {segment.icon === "envelope" && <Envelope size={16} className="text-blue-500" />}
                      {segment.icon === "tag" && <Tag size={16} className="text-purple-500" />}
                      <span className="font-medium text-sm">{segment.name}</span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">{segment.description}</p>
                  </button>
                ))}
              </div>

              {/* Tag selector for "by_tag" segment */}
              {selectedSegmentId === "by_tag" && (
                <div className="pt-2">
                  <Label className="text-sm">Select Tag</Label>
                  <Select
                    value={selectedSegmentTag}
                    onValueChange={setSelectedSegmentTag}
                  >
                    <SelectTrigger className="mt-1" data-testid="segment-tag-select">
                      <SelectValue placeholder="Choose a tag..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableTags.map((tag) => (
                        <SelectItem key={tag} value={tag}>
                          {tag}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {availableTags.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      No tags found. Add tags to customers first.
                    </p>
                  )}
                </div>
              )}

              {/* Segment recipient count */}
              <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                <span className="text-sm text-zinc-600">Recipients in this segment:</span>
                <Badge className={segmentRecipientCount > 0 ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-600"}>
                  {loadingSegmentCount ? (
                    <SpinnerGap size={12} className="animate-spin" />
                  ) : (
                    <>{segmentRecipientCount} customers</>
                  )}
                </Badge>
              </div>
            </CardContent>
          </Card>
          
          {/* Compose Card */}
          <Card className="border-2 border-zinc-200">
            <CardHeader>
              <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                <PaperPlaneTilt size={20} className="text-[#FF6600]" />
                Compose Email
              </CardTitle>
              <CardDescription>Create and send an email to all eligible customers</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Subject */}
              <div>
                <Label>Subject *</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g., Spring Special - 20% Off All Services!"
                  className="mt-1"
                  data-testid="subject-input"
                />
              </div>
              
              {/* Body Editor */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Email Body *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setFullScreenPreviewOpen(true)}
                    className="border-2 h-8"
                    data-testid="preview-btn"
                  >
                    <Eye size={14} className="mr-1" />
                    Preview
                  </Button>
                </div>
                <div className="border-2 border-zinc-200 rounded-lg overflow-hidden">
                  <ReactQuill
                    theme="snow"
                    value={body}
                    onChange={handleBodyChange}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Write your email content here..."
                    className="bg-white"
                    style={{ minHeight: '200px' }}
                    data-testid="body-editor"
                  />
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  An unsubscribe link will be automatically added to all emails.
                </p>
              </div>

              {/* Scheduling Option */}
              <div className="p-4 bg-zinc-50 rounded-lg border border-zinc-200">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Clock size={18} className="text-zinc-500" />
                    <Label className="font-medium">Schedule for Later</Label>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isScheduled}
                      onChange={(e) => setIsScheduled(e.target.checked)}
                      className="sr-only peer"
                      data-testid="schedule-toggle"
                    />
                    <div className="w-11 h-6 bg-zinc-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF6600]"></div>
                  </label>
                </div>
                {isScheduled && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-zinc-500">Date</Label>
                      <Input
                        type="date"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="mt-1"
                        data-testid="schedule-date"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-zinc-500">Time</Label>
                      <Input
                        type="time"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        className="mt-1"
                        data-testid="schedule-time"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Send Test Email Section */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Flask size={18} className="text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">Send Test Email</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {showTestEmailInput ? (
                      <div className="flex items-center gap-2">
                        <Input
                          type="email"
                          placeholder="Override email..."
                          value={testEmailOverride}
                          onChange={(e) => setTestEmailOverride(e.target.value)}
                          className="h-8 w-48 text-sm"
                          data-testid="test-email-override-input"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setShowTestEmailInput(false);
                            setTestEmailOverride(lastOverrideEmail);
                          }}
                          className="h-8 text-xs text-zinc-500"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowTestEmailInput(true)}
                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                        data-testid="test-email-edit-btn"
                      >
                        {userEmail ? "Send to different email" : "Enter email"}
                      </button>
                    )}
                    <Button
                      onClick={handleSendTest}
                      disabled={sendingTest || (!userEmail && !testEmailOverride.trim())}
                      variant="outline"
                      size="sm"
                      className="h-8 border-blue-300 text-blue-700 hover:bg-blue-100"
                      data-testid="send-test-btn"
                    >
                      {sendingTest ? (
                        <SpinnerGap size={14} className="animate-spin" />
                      ) : (
                        <>
                          <EnvelopeSimple size={14} className="mr-1" />
                          Send Test
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  {showTestEmailInput && testEmailOverride.trim() 
                    ? `Test will be sent to: ${testEmailOverride.trim()}`
                    : userEmail 
                      ? `Test will be sent to: ${userEmail}`
                      : "Enter an email address to send a test"
                  }
                </p>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-zinc-200">
                <div className="flex items-center gap-2 text-sm text-zinc-600">
                  <Users size={16} />
                  <span>
                    <strong>{getSegmentDisplayName()}</strong> — {loadingSegmentCount ? "..." : segmentRecipientCount} recipients
                  </span>
                </div>
                <Button
                  onClick={handleSend}
                  disabled={sending || segmentRecipientCount === 0}
                  className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
                  data-testid="send-btn"
                >
                  {sending ? (
                    <SpinnerGap size={18} className="animate-spin" />
                  ) : isScheduled ? (
                    <>
                      <Clock size={18} className="mr-2" />
                      Schedule Campaign
                    </>
                  ) : (
                    <>
                      <PaperPlaneTilt size={18} className="mr-2" />
                      Send Now
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Scheduled Campaigns Section */}
          {scheduledCampaigns.length > 0 && (
            <Card className="border-2 border-purple-200 bg-purple-50/30">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                  <Clock size={20} className="text-purple-600" />
                  Scheduled Campaigns
                  <Badge className="bg-purple-100 text-purple-800 ml-2">
                    {scheduledCampaigns.length} pending
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Campaigns scheduled to send automatically (times shown in {tenantTimezone})
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {scheduledCampaigns.map((campaign) => (
                    <div 
                      key={campaign.campaign_id} 
                      className="p-4 bg-white rounded-lg border border-purple-200 flex items-center gap-4"
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        campaign.status === "sending" ? "bg-blue-100" : "bg-purple-100"
                      }`}>
                        {campaign.status === "sending" ? (
                          <SpinnerGap size={20} className="text-blue-600 animate-spin" />
                        ) : (
                          <Clock size={20} className="text-purple-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{campaign.subject}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className="bg-zinc-100 text-zinc-600 text-xs">
                            {campaign.segment_name || "All Customers"}
                          </Badge>
                          <span className="text-xs text-zinc-500">
                            {campaign.recipients_count} recipients
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-purple-700">
                          {formatScheduledDate(campaign.scheduled_for)}
                        </p>
                        <Badge className={campaign.status === "sending" ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800"}>
                          {campaign.status === "sending" ? "Sending..." : "Scheduled"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        {campaign.status === "scheduled" && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenReschedule(campaign)}
                              disabled={actionLoading === campaign.campaign_id}
                              className="text-xs text-purple-600 hover:text-purple-800 hover:bg-purple-100"
                              data-testid={`reschedule-${campaign.campaign_id}`}
                            >
                              <Clock size={14} className="mr-1" />
                              Edit Time
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCancelScheduledCampaign(campaign.campaign_id)}
                              disabled={actionLoading === campaign.campaign_id}
                              className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`cancel-${campaign.campaign_id}`}
                            >
                              {actionLoading === campaign.campaign_id ? (
                                <SpinnerGap size={14} className="animate-spin" />
                              ) : (
                                <>
                                  <XCircle size={14} className="mr-1" />
                                  Cancel
                                </>
                              )}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Campaigns */}
          {campaigns.length > 0 && (
            <Card className="border-2 border-zinc-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                  <ChartLine size={20} className="text-[#FF6600]" />
                  Recent Campaigns
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="divide-y divide-zinc-100">
                  {campaigns.map((campaign) => {
                    const getStatusColor = () => {
                      if (campaign.status === "sent") return "bg-emerald-100";
                      if (campaign.status === "failed") return "bg-red-100";
                      if (campaign.status === "partial") return "bg-amber-100";
                      if (campaign.status === "sending" || campaign.status === "queued") return "bg-blue-100";
                      if (campaign.status === "cancelled") return "bg-zinc-100";
                      return "bg-zinc-100";
                    };
                    const getStatusIcon = () => {
                      if (campaign.status === "sent") return <CheckCircle size={20} className="text-emerald-600" />;
                      if (campaign.status === "failed") return <XCircle size={20} className="text-red-600" />;
                      if (campaign.status === "partial") return <Warning size={20} className="text-amber-600" />;
                      if (campaign.status === "cancelled") return <XCircle size={20} className="text-zinc-400" />;
                      return <Clock size={20} className="text-blue-600" />;
                    };
                    const getBadgeClass = () => {
                      if (campaign.status === "sent") return "bg-emerald-100 text-emerald-800";
                      if (campaign.status === "failed") return "bg-red-100 text-red-800";
                      if (campaign.status === "cancelled") return "bg-zinc-100 text-zinc-600";
                      return "bg-amber-100 text-amber-800";
                    };
                    
                    return (
                      <div key={campaign.campaign_id} className="py-3 flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getStatusColor()}`}>
                          {getStatusIcon()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{campaign.subject}</p>
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-zinc-500">{formatDate(campaign.created_at)}</p>
                            {campaign.error_summary && (
                              <span className="text-xs text-red-500 truncate max-w-[200px]" title={campaign.error_summary}>
                                Error: {campaign.error_summary}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex items-center gap-2">
                          <Badge className={getBadgeClass()}>
                            {campaign.status === "cancelled" ? "Cancelled" : `${campaign.sent_count}/${campaign.recipients_count} sent`}
                          </Badge>
                          {campaign.status === "failed" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRetryFailedCampaign(campaign.campaign_id)}
                              disabled={actionLoading === campaign.campaign_id}
                              className="text-xs text-[#FF6600] hover:text-[#CC5200] hover:bg-[#FF6600]/10"
                              data-testid={`retry-${campaign.campaign_id}`}
                            >
                              {actionLoading === campaign.campaign_id ? (
                                <SpinnerGap size={14} className="animate-spin" />
                              ) : (
                                <>
                                  <ArrowsClockwise size={14} className="mr-1" />
                                  Retry
                                </>
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedCampaign(campaign.campaign_id)}
                            className="text-xs text-zinc-500 hover:text-[#FF6600]"
                            data-testid={`view-report-${campaign.campaign_id}`}
                          >
                            View Report
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        </>
      )}
      
      {/* Full Screen Preview Modal */}
      <FullScreenPreviewModal
        isOpen={fullScreenPreviewOpen}
        onClose={() => setFullScreenPreviewOpen(false)}
        onRefresh={handleRefreshPreview}
        previewWidth={previewWidth}
        onWidthChange={setPreviewWidth}
        loading={previewLoading}
        subject={subject}
      >
        <EmailTemplatePreview
          templateId={selectedTemplateId}
          templateName={marketingTemplates.find(t => t.template_id === selectedTemplateId)?.name}
          subject={subject || "Your Subject Here"}
          body={committedBody || "<p>Start typing your message above...</p>"}
          onWidthChange={setPreviewWidth}
          hideWidthToggle={true}
        />
      </FullScreenPreviewModal>
      
      {/* Recipients Drawer */}
      <RecipientsDrawer
        isOpen={recipientsDrawerOpen}
        onClose={() => setRecipientsDrawerOpen(false)}
        eligibleRecipients={eligibleRecipients}
        optedOutRecipients={optedOutRecipients}
        onToggleOptOut={toggleOptOut}
        onRefresh={fetchSegmentRecipientsForModal}
        segmentName={getSegmentDisplayName()}
      />

      {/* Confirm Send Modal - Enhanced with segment info and marketing disclaimer */}
      <Dialog open={confirmModalOpen} onOpenChange={setConfirmModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase flex items-center gap-2">
              <Megaphone size={20} className="text-[#FF6600]" />
              {isScheduled ? "Confirm Marketing Campaign" : "Send Marketing Campaign"}
            </DialogTitle>
            <DialogDescription>
              Review the details below before sending this marketing email.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Marketing Email Disclaimer */}
            <div className="p-3 bg-[#FF6600]/10 border border-[#FF6600]/30 rounded-lg">
              <p className="text-sm text-[#CC5200] font-medium flex items-center gap-2">
                <Megaphone size={16} />
                This is a MARKETING email
              </p>
              <p className="text-xs text-[#CC5200]/80 mt-1">
                This email will be sent to customers who have opted in to marketing communications.
                An unsubscribe link will be included automatically.
              </p>
            </div>

            {/* Subject */}
            <div className="p-3 bg-zinc-50 rounded-lg">
              <p className="text-xs text-zinc-500 mb-1">Subject</p>
              <p className="font-medium">{subject}</p>
            </div>

            {/* Template */}
            {getSelectedTemplateName && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm text-emerald-800 flex items-center gap-2">
                  <ImageIcon size={16} />
                  Template: {getSelectedTemplateName}
                </p>
              </div>
            )}

            {/* Segment + Recipient Count */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800 flex items-center gap-2">
                <Users size={16} />
                Segment: <strong>{getSegmentDisplayName()}</strong>
              </p>
              <p className="text-xs text-blue-600 mt-1">
                {segmentRecipientCount} recipients will receive this email
              </p>
            </div>
            
            {/* Schedule info */}
            {isScheduled && scheduledDate && scheduledTime && (
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm text-purple-800 flex items-center gap-2">
                  <Clock size={16} />
                  Scheduled: {new Date(`${scheduledDate}T${scheduledTime}`).toLocaleString()}
                </p>
              </div>
            )}
            
            {/* Warning */}
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <Warning size={18} className="text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                {isScheduled 
                  ? "You can cancel a scheduled campaign before it's sent."
                  : "This action cannot be undone. Emails will be sent immediately."
                }
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setConfirmModalOpen(false)}
              className="flex-1 border-2"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmSend}
              className="flex-1 bg-[#FF6600] hover:bg-[#CC5200] text-white"
              data-testid="confirm-send-btn"
            >
              {isScheduled ? (
                <>
                  <Clock size={18} className="mr-2" />
                  Schedule Campaign
                </>
              ) : (
                <>
                  <PaperPlaneTilt size={18} className="mr-2" />
                  Send to {segmentRecipientCount} Recipients
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Campaign Report Modal */}
      <Dialog open={!!selectedCampaign} onOpenChange={(open) => !open && setSelectedCampaign(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase flex items-center gap-2">
              <ChartLine size={20} className="text-[#FF6600]" />
              Campaign Report
            </DialogTitle>
            <DialogDescription>
              Detailed send report with per-recipient results
            </DialogDescription>
          </DialogHeader>
          
          {loadingDetails ? (
            <div className="flex items-center justify-center py-8">
              <SpinnerGap size={32} className="animate-spin text-[#FF6600]" />
            </div>
          ) : campaignDetails ? (
            <div className="space-y-4">
              {/* Campaign Summary */}
              <div className="p-4 bg-zinc-50 rounded-sm">
                <p className="font-medium">{campaignDetails.campaign.subject}</p>
                <p className="text-sm text-zinc-500">
                  Sent {formatDate(campaignDetails.campaign.created_at)} by {campaignDetails.campaign.sent_by}
                </p>
              </div>
              
              {/* Status Counts */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-emerald-50 rounded-sm text-center">
                  <p className="text-2xl font-bold text-emerald-700">{campaignDetails.status_counts.delivered}</p>
                  <p className="text-xs text-emerald-600">Delivered</p>
                </div>
                <div className="p-3 bg-red-50 rounded-sm text-center">
                  <p className="text-2xl font-bold text-red-700">{campaignDetails.status_counts.failed}</p>
                  <p className="text-xs text-red-600">Failed</p>
                </div>
                <div className="p-3 bg-zinc-100 rounded-sm text-center">
                  <p className="text-2xl font-bold text-zinc-700">{campaignDetails.status_counts.queued}</p>
                  <p className="text-xs text-zinc-600">Queued</p>
                </div>
              </div>
              
              {/* First Error */}
              {campaignDetails.campaign.first_error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-sm">
                  <p className="text-sm font-medium text-red-800 flex items-center gap-2">
                    <XCircle size={16} />
                    Error: {campaignDetails.campaign.first_error}
                  </p>
                </div>
              )}
              
              {/* Recipient Details */}
              <div>
                <h4 className="font-medium text-sm mb-2">Recipient Details</h4>
                <div className="max-h-[300px] overflow-y-auto border border-zinc-200 rounded-sm">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2 font-medium">Email</th>
                        <th className="text-left p-2 font-medium">Status</th>
                        <th className="text-left p-2 font-medium">Error</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {campaignDetails.recipient_sends.map((r) => (
                        <tr key={r.send_id} className={r.status === "failed" ? "bg-red-50/50" : ""}>
                          <td className="p-2 truncate max-w-[200px]" title={r.email}>{r.email}</td>
                          <td className="p-2">
                            {r.status === "delivered" && (
                              <Badge className="bg-emerald-100 text-emerald-800 text-xs">
                                <CheckCircle size={10} className="mr-1" />
                                Delivered
                              </Badge>
                            )}
                            {r.status === "failed" && (
                              <Badge className="bg-red-100 text-red-800 text-xs">
                                <XCircle size={10} className="mr-1" />
                                Failed
                              </Badge>
                            )}
                            {r.status === "queued" && (
                              <Badge className="bg-zinc-100 text-zinc-800 text-xs">
                                <Clock size={10} className="mr-1" />
                                Queued
                              </Badge>
                            )}
                          </td>
                          <td className="p-2 text-red-600 text-xs truncate max-w-[150px]" title={r.provider_error}>
                            {r.provider_error || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center text-zinc-500 py-4">Failed to load campaign details</p>
          )}
          
          <div className="pt-4 border-t border-zinc-200">
            <Button
              variant="outline"
              onClick={() => setSelectedCampaign(null)}
              className="w-full border-2"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Saved Drafts Modal */}
      <Dialog open={showDraftsModal} onOpenChange={setShowDraftsModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase flex items-center gap-2">
              <FolderOpen size={20} className="text-[#FF6600]" />
              Saved Drafts
            </DialogTitle>
            <DialogDescription>
              Continue working on a previously saved draft
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {loadingDrafts ? (
              <div className="flex items-center justify-center py-8">
                <SpinnerGap size={24} className="animate-spin text-zinc-400" />
              </div>
            ) : drafts.length === 0 ? (
              <div className="text-center py-8">
                <FloppyDisk size={48} className="mx-auto text-zinc-300 mb-3" />
                <p className="text-zinc-500">No saved drafts</p>
                <p className="text-xs text-zinc-400 mt-1">
                  Click &ldquo;Save Draft&rdquo; while composing to save your work
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {drafts.map((draft) => (
                  <div
                    key={draft.draft_id}
                    className={`p-3 border-2 rounded-lg transition-all ${
                      currentDraftId === draft.draft_id 
                        ? "border-[#FF6600] bg-[#FF6600]/5" 
                        : "border-zinc-200 hover:border-zinc-300"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">
                          {draft.name || "Untitled Draft"}
                        </p>
                        {draft.subject && (
                          <p className="text-xs text-zinc-500 truncate mt-1">
                            Subject: {draft.subject}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className="bg-zinc-100 text-zinc-600 text-xs">
                            {segments.find(s => s.id === draft.segment_id)?.name || "All Customers"}
                          </Badge>
                          <span className="text-xs text-zinc-400">
                            {new Date(draft.updated_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleLoadDraft(draft.draft_id)}
                          className="h-8 px-2 text-[#FF6600] hover:text-[#CC5200] hover:bg-[#FF6600]/10"
                          data-testid={`load-draft-${draft.draft_id}`}
                        >
                          <FolderOpen size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDraft(draft.draft_id)}
                          className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-draft-${draft.draft_id}`}
                        >
                          <Trash size={16} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowDraftsModal(false)}
              className="flex-1 border-2"
            >
              Close
            </Button>
            <Button
              onClick={() => {
                handleNewDraft();
                setShowDraftsModal(false);
              }}
              className="flex-1 bg-[#FF6600] hover:bg-[#CC5200] text-white"
            >
              <PaperPlaneTilt size={18} className="mr-2" />
              New Email
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule Campaign Modal */}
      <Dialog open={showRescheduleModal} onOpenChange={setShowRescheduleModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase flex items-center gap-2">
              <Clock size={20} className="text-purple-600" />
              Reschedule Campaign
            </DialogTitle>
            <DialogDescription>
              Change the scheduled send time for this campaign
            </DialogDescription>
          </DialogHeader>
          
          {rescheduleTarget && (
            <div className="py-4 space-y-4">
              {/* Campaign Info */}
              <div className="p-3 bg-zinc-50 rounded-lg">
                <p className="text-sm font-medium">{rescheduleTarget.subject}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  Currently scheduled: {formatScheduledDate(rescheduleTarget.scheduled_for)}
                </p>
              </div>

              {/* New Date/Time Pickers */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-500">New Date</Label>
                  <Input
                    type="date"
                    value={newScheduledDate}
                    onChange={(e) => setNewScheduledDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="mt-1"
                    data-testid="reschedule-date"
                  />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">New Time</Label>
                  <Input
                    type="time"
                    value={newScheduledTime}
                    onChange={(e) => setNewScheduledTime(e.target.value)}
                    className="mt-1"
                    data-testid="reschedule-time"
                  />
                </div>
              </div>

              {/* Timezone Notice */}
              <p className="text-xs text-zinc-500 text-center">
                Times are in your business timezone: <strong>{tenantTimezone}</strong>
              </p>
            </div>
          )}
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowRescheduleModal(false);
                setRescheduleTarget(null);
              }}
              className="flex-1 border-2"
            >
              Cancel
            </Button>
            <Button
              onClick={handleReschedule}
              disabled={actionLoading === rescheduleTarget?.campaign_id}
              className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="confirm-reschedule-btn"
            >
              {actionLoading === rescheduleTarget?.campaign_id ? (
                <SpinnerGap size={18} className="animate-spin" />
              ) : (
                <>
                  <Clock size={18} className="mr-2" />
                  Update Schedule
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Marketing Upgrade Modal - ACTION-BASED GATING */}
      {/* Shown only when user attempts to SEND or SCHEDULE a campaign without entitlement */}
      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent data-testid="email-marketing-upgrade-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase flex items-center gap-2">
              <Lightning size={24} className="text-[#FF6600]" />
              Upgrade to Email Marketing
            </DialogTitle>
            <DialogDescription className="text-zinc-600">
              You&apos;ve set up your campaign - upgrade to send it!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-gradient-to-r from-[#FF6600]/10 to-amber-50 border border-[#FF6600]/30 rounded-lg">
              <div className="flex items-start gap-3">
                <Envelope size={24} className="text-[#FF6600] mt-0.5" />
                <div>
                  <p className="font-medium text-zinc-800">Ready to send your campaign?</p>
                  <ul className="text-sm text-zinc-600 mt-2 space-y-1">
                    <li>• Send promotional emails to all customers</li>
                    <li>• Schedule campaigns for the perfect time</li>
                    <li>• Track opens, clicks, and engagement</li>
                    <li>• Unlimited email sends</li>
                  </ul>
                </div>
              </div>
            </div>
            {featureDetails && (
              <p className="text-sm text-zinc-500 text-center">
                Starting at <span className="font-bold text-[#FF6600]">${(featureDetails.price_monthly || 0).toFixed(2)}/mo</span>
              </p>
            )}
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowUpgradeModal(false)} className="flex-1 border-2">
                Maybe Later
              </Button>
              <Button 
                onClick={() => navigate("/settings/upgrades?feature=email_marketing")} 
                className="flex-1 bg-[#FF6600] hover:bg-[#CC5200] text-white"
                data-testid="email-marketing-upgrade-btn"
              >
                <Lightning size={18} className="mr-2" />
                Upgrade Now
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
